/* eslint-disable max-lines-per-function */
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/Arbeitsbereich';
import { KalenderWoche } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/KalenderWoche';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/Mitarbeiter';
import { RowDivider } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/RowDivider';
import { ArbeitsbereichKalenderStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/ArbeitsbereichKalenderStation';
import { MitarbeiterKalenderStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/station/MitarbeiterKalenderStation';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { CSSProperties, Fragment, useRef } from 'react';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';

type StyleProps = CSSProperties & {
  '--col-start': CSSProperties['gridColumnStart'];
};

type StylePropsGrid = CSSProperties & {
  '--col-count': CSSProperties['gridTemplateColumns'];
};

export function Kalender({
  jahr,
  view,
}: {
  jahr: string;
  view: 'mitarbeiter' | 'arbeitsbereiche';
}) {
  const {
    store: {
      data: { rotationsplaene, rotationsplaeneByArbeitsbereiche },
      setKalenderScrolling,
    },
  } = useRotationsplanFeatureContext();
  const weeksInYear = dayjs(`${jahr}-01-01`).isoWeeksInYear();
  const gridRef = useRef<HTMLDivElement | null>(undefined);

  return (
    <div className="w-full relative">
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-linear-to-l from-white to-transparent z-10" />
      <div
        className="overflow-x-auto"
        onScroll={() => setKalenderScrolling(true)}
        // eslint-disable-next-line react/no-unknown-property
        onScrollEnd={() => setKalenderScrolling(false)}>
        <div
          ref={gridRef}
          className={`grid grid-cols-[1fr_repeat(var(--col-count),8px)] md:grid-cols-[180px_repeat(var(--col-count),16px)] w-fit h-full`}
          style={
            {
              '--col-count': weeksInYear * 7,
            } as StylePropsGrid
          }>
          <div className="sticky left-0 bg-white z-10 row-start-1 border-r border-solid border-neutrals-200"></div>
          {Array.from({ length: weeksInYear }, (_, i) => {
            const startOfWeek = dayjs()
              .year(parseInt(jahr))
              .isoWeek(i + 1)
              .startOf('isoWeek');

            const endOfWeek = dayjs()
              .year(parseInt(jahr))
              .isoWeek(i + 1)
              .endOf('isoWeek');

            const isCurrentWeek =
              dayjs().get('year').toString() === jahr &&
              dayjs().isoWeek() === i + 1;

            return (
              <div
                key={i}
                className={`col-start-(--col-start) col-span-7 row-start-1 p-2`}
                style={
                  {
                    '--col-start': i * 7 + 2,
                  } as StyleProps
                }>
                <KalenderWoche
                  isCurrentWeek={isCurrentWeek}
                  startOfWeek={startOfWeek.format('DD.MM')}
                  endOfWeek={endOfWeek.format('DD.MM')}
                  index={i}
                  gridRef={gridRef}
                />
              </div>
            );
          })}
          <RowDivider />
          {view === 'mitarbeiter'
            ? rotationsplaene.map((rotationsplan) => {
                return (
                  <Fragment key={rotationsplan.id}>
                    <Mitarbeiter
                      lehrjahr={rotationsplan.getCurrentLehrjahr()}
                      mitarbeiter={rotationsplan.mitarbeiter}
                      beruf={rotationsplan.beruf}
                    />
                    {rotationsplan
                      .getAllStationen()
                      .filter(
                        (station) =>
                          dayjs(station.von).get('year').toString() === jahr &&
                          dayjs(station.bis).get('year').toString() === jahr,
                      )
                      .filter(
                        (station) => station instanceof ArbeitsbereichStation,
                      )
                      .map((station) => (
                        <MitarbeiterKalenderStation
                          key={station.id}
                          station={station}
                        />
                      ))}
                    <RowDivider />
                  </Fragment>
                );
              })
            : rotationsplaeneByArbeitsbereiche.map((arbeitsbereich) => {
                return (
                  <>
                    <Arbeitsbereich arbeitsbereich={arbeitsbereich} />
                    {arbeitsbereich.stationen
                      .filter(
                        (station) =>
                          dayjs(station.von).get('year').toString() === jahr &&
                          dayjs(station.bis).get('year').toString() === jahr,
                      )
                      .map((station) => (
                        <ArbeitsbereichKalenderStation
                          key={station.id}
                          station={station}
                          arbeitsbereich={arbeitsbereich}
                        />
                      ))}
                    <RowDivider />
                  </>
                );
              })}
        </div>
      </div>
    </div>
  );
}
